<template>
  <div class="gm-panel grid-config-panel gm-scroll">
    <h3 class="gm-title config-title">Grid Configuration</h3>

    <div v-if="!gridConfig" class="gm-empty">
      <p class="gm-empty__hint">No map loaded</p>
    </div>

    <div v-else class="config-content">
      <!-- Grid Dimensions -->
      <div class="config-section">
        <h4 class="gm-section-title">Grid Dimensions</h4>

        <div class="config-row">
          <label class="config-label">Columns:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.columns"
            @input="updateColumns"
            min="5"
            max="100"
          />
        </div>

        <div class="config-row">
          <label class="config-label">Rows:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.rows"
            @input="updateRows"
            min="5"
            max="100"
          />
        </div>
      </div>

      <!-- Cell Size -->
      <div class="config-section">
        <h4 class="gm-section-title">Cell Size (pixels)</h4>

        <div class="config-row">
          <label class="config-label">Width:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.cellWidth"
            @input="updateCellWidth"
            min="16"
            max="256"
          />
        </div>

        <div class="config-row">
          <label class="config-label">Height:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.cellHeight"
            @input="updateCellHeight"
            min="16"
            max="256"
          />
        </div>
      </div>

      <!-- Perspective Morph (Legacy) -->
      <div class="config-section">
        <h4 class="gm-section-title">Perspective Morph (Legacy)</h4>
        <p class="section-hint">Simple axis-based morphing (-1.0 to 1.0)</p>

        <div class="config-row">
          <label class="config-label">X Morph:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.xMorph"
            @input="updateXMorph"
            min="-1"
            max="1"
            step="0.1"
          />
        </div>

        <div class="config-row">
          <label class="config-label">Y Morph:</label>
          <input
            type="number"
            class="gm-input config-input"
            :value="gridConfig.yMorph"
            @input="updateYMorph"
            min="-1"
            max="1"
            step="0.1"
          />
        </div>
      </div>

      <!-- 4-Point Morphing -->
      <div class="config-section">
        <h4 class="gm-section-title">4-Point Morphing</h4>
        <p class="section-hint">Adjust corner positions for perspective/rhomboid (-1.0 to 1.0)</p>

        <!-- Top Left Corner -->
        <div class="corner-group">
          <label class="corner-label">Top Left:</label>
          <div class="corner-inputs">
            <div class="config-row-compact">
              <label class="config-label-small">X:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.topLeft.x"
                @input="updateCornerOffset('topLeft', 'x', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
            <div class="config-row-compact">
              <label class="config-label-small">Y:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.topLeft.y"
                @input="updateCornerOffset('topLeft', 'y', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
          </div>
        </div>

        <!-- Top Right Corner -->
        <div class="corner-group">
          <label class="corner-label">Top Right:</label>
          <div class="corner-inputs">
            <div class="config-row-compact">
              <label class="config-label-small">X:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.topRight.x"
                @input="updateCornerOffset('topRight', 'x', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
            <div class="config-row-compact">
              <label class="config-label-small">Y:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.topRight.y"
                @input="updateCornerOffset('topRight', 'y', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
          </div>
        </div>

        <!-- Bottom Left Corner -->
        <div class="corner-group">
          <label class="corner-label">Bottom Left:</label>
          <div class="corner-inputs">
            <div class="config-row-compact">
              <label class="config-label-small">X:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.bottomLeft.x"
                @input="updateCornerOffset('bottomLeft', 'x', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
            <div class="config-row-compact">
              <label class="config-label-small">Y:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.bottomLeft.y"
                @input="updateCornerOffset('bottomLeft', 'y', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
          </div>
        </div>

        <!-- Bottom Right Corner -->
        <div class="corner-group">
          <label class="corner-label">Bottom Right:</label>
          <div class="corner-inputs">
            <div class="config-row-compact">
              <label class="config-label-small">X:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.bottomRight.x"
                @input="updateCornerOffset('bottomRight', 'x', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
            <div class="config-row-compact">
              <label class="config-label-small">Y:</label>
              <input
                type="number"
                class="gm-input config-input-small"
                :value="cornerOffsets.bottomRight.y"
                @input="updateCornerOffset('bottomRight', 'y', $event)"
                min="-1"
                max="1"
                step="0.05"
              />
            </div>
          </div>
        </div>

        <!-- Reset Button -->
        <button class="gm-btn reset-button" @click="resetCornerOffsets">
          Reset to Default
        </button>
      </div>

      <!-- Grid Info -->
      <div class="config-section info-section">
        <h4 class="gm-section-title">Grid Info</h4>
        <div class="info-row">
          <span class="info-label">Total Cells:</span>
          <span class="info-value">{{ totalCells }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Grid Size:</span>
          <span class="info-value">{{ gridWidth }} × {{ gridHeight }} px</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'GridConfigPanel',
  computed: {
    ...mapGetters('tabletopEditor', [
      'gridConfig'
    ]),
    totalCells() {
      if (!this.gridConfig) return 0;
      return this.gridConfig.columns * this.gridConfig.rows;
    },
    gridWidth() {
      if (!this.gridConfig) return 0;
      return this.gridConfig.columns * this.gridConfig.cellWidth;
    },
    gridHeight() {
      if (!this.gridConfig) return 0;
      return this.gridConfig.rows * this.gridConfig.cellHeight;
    },
    cornerOffsets() {
      if (!this.gridConfig || !this.gridConfig.cornerOffsets) {
        return {
          topLeft: { x: 0, y: 0 },
          topRight: { x: 0, y: 0 },
          bottomLeft: { x: 0, y: 0 },
          bottomRight: { x: 0, y: 0 }
        };
      }
      return this.gridConfig.cornerOffsets;
    }
  },
  methods: {
    ...mapActions('tabletopEditor', [
      'updateGridConfig'
    ]),
    updateColumns(event) {
      const value = parseInt(event.target.value);
      if (!isNaN(value) && value >= 5 && value <= 100) {
        this.updateGridConfig({ columns: value });
      }
    },
    updateRows(event) {
      const value = parseInt(event.target.value);
      if (!isNaN(value) && value >= 5 && value <= 100) {
        this.updateGridConfig({ rows: value });
      }
    },
    updateCellWidth(event) {
      const value = parseInt(event.target.value);
      if (!isNaN(value) && value >= 16 && value <= 256) {
        this.updateGridConfig({ cellWidth: value });
      }
    },
    updateCellHeight(event) {
      const value = parseInt(event.target.value);
      if (!isNaN(value) && value >= 16 && value <= 256) {
        this.updateGridConfig({ cellHeight: value });
      }
    },
    updateXMorph(event) {
      const value = parseFloat(event.target.value);
      if (!isNaN(value) && value >= -1 && value <= 1) {
        this.updateGridConfig({ xMorph: value });
      }
    },
    updateYMorph(event) {
      const value = parseFloat(event.target.value);
      if (!isNaN(value) && value >= -1 && value <= 1) {
        this.updateGridConfig({ yMorph: value });
      }
    },
    updateCornerOffset(corner, axis, event) {
      const value = parseFloat(event.target.value);
      if (!isNaN(value) && value >= -1 && value <= 1) {
        const newCornerOffsets = {
          ...this.cornerOffsets,
          [corner]: {
            ...this.cornerOffsets[corner],
            [axis]: value
          }
        };
        this.updateGridConfig({ cornerOffsets: newCornerOffsets });
      }
    },
    resetCornerOffsets() {
      const defaultOffsets = {
        topLeft: { x: 0, y: 0 },
        topRight: { x: 0, y: 0 },
        bottomLeft: { x: 0, y: 0 },
        bottomRight: { x: 0, y: 0 }
      };
      this.updateGridConfig({ cornerOffsets: defaultOffsets });
    }
  }
};
</script>

<style scoped>
.grid-config-panel {
  max-height: 37.5rem;
}

.config-title {
  margin: 0 0 var(--gm-gap);
}

.config-content {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
}

.config-section {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap);
}

.section-hint {
  margin: 0 0 var(--gm-gap-sm);
  font-size: 0.68rem;
  color: var(--gm-text-dim);
  font-style: italic;
}

.config-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--gm-gap-sm) 0;
  gap: var(--gm-gap-sm);
}

.config-label {
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  font-weight: 500;
  flex: 1;
}

.config-input {
  width: 5rem;
  text-align: right;
}

.info-section {
  background: var(--gm-accent-softer);
  border-color: var(--gm-border);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
}

.info-label {
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  font-weight: 500;
}

.info-value {
  font-size: 0.75rem;
  color: var(--gm-accent);
  font-weight: 700;
}

/* 4-Point Morphing Styles */
.corner-group {
  margin-bottom: var(--gm-gap-sm);
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
}

.corner-label {
  display: block;
  font-family: var(--gm-font-display);
  font-size: 0.68rem;
  color: var(--gm-accent);
  font-weight: 600;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.corner-inputs {
  display: flex;
  gap: var(--gm-gap-sm);
}

.config-row-compact {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 1;
}

.config-label-small {
  font-size: 0.68rem;
  color: var(--gm-text-muted);
  font-weight: 500;
  min-width: 0.9rem;
}

.config-input-small {
  flex: 1;
  padding: 0.25rem 0.35rem;
  font-size: 0.7rem;
  text-align: right;
}

.reset-button {
  width: 100%;
  margin-top: var(--gm-gap-sm);
}
</style>
